import { Matrix, SelectedResponse } from '@focaldata/cin-ui-components'
import {
  CfmData,
  CfmContext
} from 'containers/QuestionFlowManager/QuestionFlowManager'
import useEntryProgress from 'hooks/useEntryProgress'
import useEntryRenderingDateTime from 'hooks/useEntryRenderingDateTime'
import useSendResponseChoice from 'hooks/useSendResponseChoice'
import { PersistentRespondentChoice } from 'model/persistentRespondentChoice'
import {
  EntrySettingValue,
  MatrixItem,
  QuestionnaireEntry,
  QuestionTypes,
  SettingCodes
} from 'model/questionnaire'
import React, { useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  getMultipleChoiceInstructions,
  getMultipleChoiceMatrixDesktopInstructions,
  getSetting
} from 'utils/question'
import persistRespondentChoice from 'utils/persistRespondentChoices'
import displayEntityBasedOnLogic from 'utils/displayEntityBasedOnLogic'
import { setHiddenByDisplayLogic } from 'utils/hiddenByDisplayLogic'
import { updateMultiSelectIntervalBoundariesSettings } from 'utils/updateSettings'
import {
  entryHasNoMatrixRows,
  getVisibleMatrixRows
} from 'utils/matrixRowsMaskingLogic'
import { getVisibleResponseOptions } from 'utils/responseOptionsMaskingLogic'
import { useQuestionTimer } from 'hooks/useQuestionTimer'
import {
  addEntriesHiddenByLooping,
  getEntriesToHideByLooping
} from 'utils/hiddenByLooping'
import { buildResponseOptionChoicesForSelectedResponses } from 'utils/buildResponseOptionChoicesForSelectedResponses'
import { useQuestionnaireParams } from '../../hooks/useQuestionnaireParams'
import { useMediaLoc } from '../../hooks/useMediaLoc'
import { getResponseChoiceIds } from './Entry.utils'

interface Props {
  entry: QuestionnaireEntry
}

const scrollTop = () => {
  setTimeout(() => {
    window.scroll({ top: 0, behavior: 'smooth' })
  }, 0)
}

const MatrixEntry: React.FC<Props> = (props: Props) => {
  const { entry }: Props = props
  const entryItem = entry.entryItem as MatrixItem
  const limit = getSetting(entryItem.settingValues, SettingCodes.ChoiceLimit)
  const progress = useEntryProgress(entry)
  const timer = useQuestionTimer(entryItem)
  const renderingDateTime = useEntryRenderingDateTime(entry)
  const sendResponseOptionChoices = useSendResponseChoice()
  const { getNextEntryPosition, preview } = useContext<CfmData>(CfmContext)
  const mandatoryParams = useQuestionnaireParams()
  const mediaLoc = useMediaLoc()

  const shouldDisplayEntityBasedOnLogic = displayEntityBasedOnLogic(
    mandatoryParams.respondentId,
    mandatoryParams.surveyId,
    entryItem.questionLogic,
    entry.entryType
  )

  if (!shouldDisplayEntityBasedOnLogic) {
    setHiddenByDisplayLogic(
      mandatoryParams.respondentId,
      mandatoryParams.surveyId,
      entryItem.matrixTitle.matrixTitleId
    )
    getNextEntryPosition()
  }

  const entryItemWithVisibleResponseOptions = getVisibleResponseOptions(
    mandatoryParams.respondentId,
    mandatoryParams.surveyId,
    entryItem
  )

  const entryItemWithFilteredMatrixRowsAndColumns = getVisibleMatrixRows(
    mandatoryParams.respondentId,
    mandatoryParams.surveyId,
    entryItemWithVisibleResponseOptions
  )

  const entryItemSettingValues: EntrySettingValue[] =
    updateMultiSelectIntervalBoundariesSettings(
      entryItemWithFilteredMatrixRowsAndColumns.settingValues,
      entryItemWithVisibleResponseOptions.responseOptions.length
    )

  if (
    entryHasNoMatrixRows(entryItemWithFilteredMatrixRowsAndColumns.matrixRows)
  ) {
    setHiddenByDisplayLogic(
      mandatoryParams.respondentId,
      mandatoryParams.surveyId,
      entryItem.matrixTitle.matrixTitleId
    )
    getNextEntryPosition()
    return null
  }

  const intervalMin =
    Number(
      getSetting(entryItemSettingValues, SettingCodes.ChoiceIntervalMin)
    ) ?? undefined
  const intervalMax =
    Number(
      getSetting(entryItemSettingValues, SettingCodes.ChoiceIntervalMax)
    ) ?? undefined

  const loc = {
    ...mediaLoc,
    next: <FormattedMessage id="question_next" defaultMessage="Next" />,
    matrixInstructions: {
      singleSelectDesktop: (
        <FormattedMessage
          id="question_matrixSelectOneAnswerDesktop"
          defaultMessage="Select one answer per row"
        />
      ),
      multiSelectDesktop: (
        <FormattedMessage
          id={getMultipleChoiceMatrixDesktopInstructions(
            limit,
            intervalMin,
            intervalMax,
            entryItem.responseOptions.length
          )}
          defaultMessage="Select up to {count} answers per row"
          values={{
            count: <b>{limit ?? intervalMax}</b>,
            min: <b>{intervalMin}</b>
          }}
        />
      ),
      singleSelectMobile: (
        <FormattedMessage
          id="question_singleChoiceInstructions"
          defaultMessage="Select one answer"
        />
      ),
      multiSelectMobile: (
        <FormattedMessage
          id={getMultipleChoiceInstructions(
            limit,
            intervalMin,
            intervalMax,
            entryItem.responseOptions.length
          )}
          defaultMessage="Select up to {count} answers"
          values={{
            count: <b>{limit ?? intervalMax}</b>,
            min: <b>{intervalMin}</b>
          }}
        />
      )
    }
  }

  const handleNext: (selectedResponses: SelectedResponse[][]) => void = (
    selectedResponses
  ) => {
    const { looping } = entry
    const persistentRespondentChoice: PersistentRespondentChoice[] = []
    selectedResponses.forEach((_, index) => {
      const { matrixTitle, matrixRows } =
        entryItemWithFilteredMatrixRowsAndColumns
      const respondentChoice = {
        questionId: matrixRows[index].question.questionId,
        matrixTitleId: matrixTitle.matrixTitleId,
        responseChoiceIds: getResponseChoiceIds(
          entryItem,
          selectedResponses[index]
        )
      }
      persistRespondentChoice(
        mandatoryParams.respondentId,
        mandatoryParams.surveyId,
        respondentChoice
      )
      persistentRespondentChoice.push(respondentChoice)
    })

    if (looping) {
      const entriesToHide = getEntriesToHideByLooping(
        looping,
        persistentRespondentChoice
      )

      addEntriesHiddenByLooping(
        mandatoryParams.respondentId,
        mandatoryParams.surveyId,
        entriesToHide
      )
    }

    if (!preview) {
      // Build all matrix row responses for potential batch submission
      const allMatrixRowResponses = selectedResponses.map(
        (selectedResponseRow, index) => ({
          questionId:
            entryItemWithFilteredMatrixRowsAndColumns.matrixRows[index].question
              .questionId,
          responseChoices: buildResponseOptionChoicesForSelectedResponses(
            selectedResponseRow,
            entryItemWithVisibleResponseOptions.responseOptions
          ),
          matrix: {
            matrixTitleId:
              entryItemWithFilteredMatrixRowsAndColumns.matrixTitle
                .matrixTitleId,
            rowPosition:
              entryItemWithFilteredMatrixRowsAndColumns.matrixRows[index]
                .position,
            renderedRowPosition:
              entryItemWithFilteredMatrixRowsAndColumns.matrixRows[index]
                .renderedPosition
          }
        })
      )

      // Send matrix responses
      // The routing logic in useSendResponseChoice will determine whether to:
      // 1. Use new categorical matrix endpoint (single API call for all rows)
      // 2. Use legacy endpoint (one call per row)
      allMatrixRowResponses.forEach((rowResponse) => {
        sendResponseOptionChoices({
          questionId: rowResponse.questionId,
          entry,
          entryType: entry.entryType,
          questionTypeCode: QuestionTypes.Matrix,
          renderingDateTime,
          responseChoices: rowResponse.responseChoices,
          matrix: rowResponse.matrix,
          allMatrixRowResponses
        })
      })
    }
    getNextEntryPosition([])
  }

  return (
    <Matrix
      item={{
        ...entryItemWithFilteredMatrixRowsAndColumns,
        settingValues: entryItemSettingValues
      }}
      timer={timer}
      progress={progress}
      preview={preview}
      loc={loc}
      next={{ disabled: false, loading: false, onNext: handleNext }}
      onMobileNext={scrollTop}
    />
  )
}

export default MatrixEntry
